/**
 * Add people — client-side entity executor (ADR-040 Q12).
 *
 * Runs in the HOST page, never in the plugin iframe. A plugin opens the host's
 * "Add people" sheet for one of its workspaces; the PERSON chooses who to add
 * (co-members of the Fias workspace they are acting in, or an exact username
 * they type) and may change the suggested role, and the host makes each add
 * from their own session through the session-only route. The plugin supplies
 * no names and learns only the people the person chose, by ref.
 *
 * Host services arrive through `configureWorkspaceAddPeopleHost` at
 * registration (`client/src/lib/register-client-entities.ts`), because the
 * executor context carries only `{ userId, archeId, holderId }`.
 *
 * Expected failures are RESULTS, not throws, so a typed code reaches the SDK.
 */

import type { ClientEntityDefinition, ClientEntityInvocationContext } from './types';

export const WORKSPACE_ADD_PEOPLE_ENTITY_ID = 'ent_intg_workspace_add_people_00';

/** Roles the plugin may SUGGEST; the person chooses, capped at their own role. */
export type AddPeopleSuggestedRole = 'admin' | 'member' | 'viewer';

export type WorkspaceAddPeopleErrorCode = 'INVALID_INPUT' | 'ADD_PEOPLE_UNAVAILABLE' | 'SHEET_BUSY';

export interface AddedPerson {
  /** The app's ref for the person. */
  ref: string;
  displayName: string;
  role: string;
}

export type WorkspaceAddPeopleResult =
  | { added: AddedPerson[] }
  | { error: { code: WorkspaceAddPeopleErrorCode; message: string } };

/** What the host page provides: the sheet, drawn for one app's workspace. */
export interface WorkspaceAddPeopleHost {
  showAddPeopleSheet(request: {
    archeId: string;
    holderId: string | null;
    workspaceId: string;
    role: AddPeopleSuggestedRole;
  }): Promise<WorkspaceAddPeopleResult>;
}

let host: WorkspaceAddPeopleHost | null = null;

/** Called once by the host page at registration. */
export function configureWorkspaceAddPeopleHost(next: WorkspaceAddPeopleHost | null): void {
  host = next;
}

const ROLES: readonly AddPeopleSuggestedRole[] = ['admin', 'member', 'viewer'];
const WORKSPACE_ID_MAX = 64;

export async function workspaceAddPeopleExecutor(
  input: unknown,
  context: ClientEntityInvocationContext,
): Promise<WorkspaceAddPeopleResult> {
  // The plugin names the workspace and may suggest a role — never a person.
  const invalid = (message: string): WorkspaceAddPeopleResult => ({
    error: { code: 'INVALID_INPUT', message },
  });
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return invalid('Pass { workspaceId, role? }');
  }
  const { workspaceId, role, ...rest } = input as Record<string, unknown>;
  if (Object.keys(rest).length > 0) {
    return invalid('Only workspaceId and role are accepted; the person chooses who to add');
  }
  if (typeof workspaceId !== 'string' || !workspaceId || workspaceId.length > WORKSPACE_ID_MAX) {
    return invalid('workspaceId is required');
  }
  if (role !== undefined && !ROLES.includes(role as AddPeopleSuggestedRole)) {
    return invalid("role is 'admin', 'member' or 'viewer'");
  }
  if (!host) {
    return {
      error: { code: 'ADD_PEOPLE_UNAVAILABLE', message: 'Adding people is not available here' },
    };
  }
  return host.showAddPeopleSheet({
    archeId: context.archeId,
    holderId: context.holderId ?? null,
    workspaceId,
    role: (role as AddPeopleSuggestedRole | undefined) ?? 'member',
  });
}

export const workspaceAddPeopleDefinition = {
  entityId: WORKSPACE_ADD_PEOPLE_ENTITY_ID,
  rateLimitPerMinute: 10,
  pluginPermission: 'data:workspace',
  executor: workspaceAddPeopleExecutor,
} as const satisfies ClientEntityDefinition<unknown, WorkspaceAddPeopleResult>;
